import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiX, FiPlus, FiEdit } from 'react-icons/fi';
import Toggle from 'react-toggle';
import 'react-toggle/style.css';
import { partnersAPI, extensionsAPI } from '../services/api';
import './AddPartner.css';

const AddPartner = () => {
  const navigate = useNavigate();
  console.log('AddPartner component rendering...');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    company: '',
    partnerType: 'partner',
    role: 'partner',
    balance: 0,
    isBalanceUnlimited: false,
    assignedExtensions: []
  });
  
  // State for extension-specific settings
  const [extensionSettings, setExtensionSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [extensions, setExtensions] = useState([]);
  const [loadingExtensions, setLoadingExtensions] = useState(true);

  // Fetch extensions on component mount
  useEffect(() => {
    const fetchExtensions = async () => {
      try {
        console.log('Fetching extensions...');
        setLoadingExtensions(true);
        const response = await extensionsAPI.getAll();
        console.log('Extensions response:', response);
        
        // Handle different response formats
        let extensionsData = [];
        if (response && response.success && response.extensions) {
          extensionsData = response.extensions;
        } else if (response && response.data) {
          extensionsData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          extensionsData = response;
        }
        
        console.log('Processed extensions data:', extensionsData);
        setExtensions(extensionsData);
        
        // Initialize extension settings
        const initialSettings = {};
        extensionsData.forEach(ext => {
          const extId = ext._id || ext.id;
          initialSettings[extId] = {
            linkManager: false,
            extensionBalance: 0,
            isExtensionBalanceUnlimited: false
          };
        });
        setExtensionSettings(initialSettings);
      } catch (error) {
        console.error('Failed to fetch extensions:', error);
        toast.error('Failed to load extensions');
        setExtensions([]); // Set empty array on error
      } finally {
        setLoadingExtensions(false);
      }
    };

    fetchExtensions();
  }, []);

  // Monitor extension balance settings and update master unlimited balance
  useEffect(() => {
    if (Object.keys(extensionSettings).length === 0) return;
    
    const allUnlimited = Object.values(extensionSettings).every(
      setting => setting.isExtensionBalanceUnlimited
    );
    
    const hasAssignedExtensions = formData.assignedExtensions.length > 0;
    
    // Only update if there are assigned extensions and the state is different
    if (hasAssignedExtensions && allUnlimited !== formData.isBalanceUnlimited) {
      setFormData(prev => ({
        ...prev,
        isBalanceUnlimited: allUnlimited
      }));
    }
  }, [extensionSettings, formData.assignedExtensions]);

  // Monitor extension balance values and update master balance
  useEffect(() => {
    if (Object.keys(extensionSettings).length === 0) return;
    
    const hasAssignedExtensions = formData.assignedExtensions.length > 0;
    
    if (hasAssignedExtensions && !formData.isBalanceUnlimited) {
      // Get all non-unlimited extension balances
      const assignedExtensionBalances = formData.assignedExtensions
        .map(extId => extensionSettings[extId])
        .filter(settings => settings && !settings.isExtensionBalanceUnlimited)
        .map(settings => settings.extensionBalance || 0);
      
      if (assignedExtensionBalances.length > 0) {
        // Check if all assigned extensions have the same balance
        const allSameBalance = assignedExtensionBalances.every(
          balance => balance === assignedExtensionBalances[0]
        );
        
        if (allSameBalance && assignedExtensionBalances[0] !== parseInt(formData.balance)) {
          setFormData(prev => ({
            ...prev,
            balance: assignedExtensionBalances[0]
          }));
        }
      }
    }
  }, [extensionSettings, formData.assignedExtensions, formData.isBalanceUnlimited]);

  // Helper function to check if all assigned extensions are synced with master
  const areExtensionsSynced = () => {
    if (formData.assignedExtensions.length === 0) return true;
    
    return formData.assignedExtensions.every(extId => {
      const settings = extensionSettings[extId];
      if (!settings) return true;
      
      // Check unlimited balance sync
      const unlimitedSynced = settings.isExtensionBalanceUnlimited === formData.isBalanceUnlimited;
      
      // Check balance value sync (only if not unlimited)
      const balanceSynced = formData.isBalanceUnlimited || 
        (settings.extensionBalance === parseInt(formData.balance));
      
      return unlimitedSynced && balanceSynced;
    });
  };

  // Helper function to check if balance values are synced
  const areBalanceValuesSynced = () => {
    if (formData.assignedExtensions.length === 0 || formData.isBalanceUnlimited) return true;
    
    return formData.assignedExtensions.every(extId => {
      const settings = extensionSettings[extId];
      return settings && settings.extensionBalance === parseInt(formData.balance);
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'isBalanceUnlimited') {
      // Master unlimited balance toggle - affects all extensions
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      
      // Update all extension balance settings
      setExtensionSettings(prev => {
        const updatedSettings = {};
        Object.keys(prev).forEach(extId => {
          updatedSettings[extId] = {
            ...prev[extId],
            isExtensionBalanceUnlimited: checked
          };
        });
        return updatedSettings;
      });
    } else if (name === 'balance') {
      // Master balance input - affects all assigned extensions
      const balanceValue = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: balanceValue
      }));
      
      // Update all assigned extension balances (only if not unlimited)
      if (!formData.isBalanceUnlimited) {
        setExtensionSettings(prev => {
          const updatedSettings = { ...prev };
          formData.assignedExtensions.forEach(extId => {
            if (updatedSettings[extId]) {
              updatedSettings[extId] = {
                ...updatedSettings[extId],
                extensionBalance: balanceValue,
                isExtensionBalanceUnlimited: false
              };
            }
          });
          return updatedSettings;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleExtensionToggle = (extensionId) => {
    setFormData(prev => ({
      ...prev,
      assignedExtensions: prev.assignedExtensions.includes(extensionId)
        ? prev.assignedExtensions.filter(id => id !== extensionId)
        : [...prev.assignedExtensions, extensionId]
    }));
  };

  const handleLinkManagerToggle = (extensionId) => {
    setExtensionSettings(prev => ({
      ...prev,
      [extensionId]: {
        ...prev[extensionId],
        linkManager: !prev[extensionId]?.linkManager
      }
    }));
  };

  const handleExtensionBalanceChange = (extensionId, value) => {
    setExtensionSettings(prev => ({
      ...prev,
      [extensionId]: {
        ...prev[extensionId],
        extensionBalance: parseInt(value) || 0
      }
    }));
  };

  const handleExtensionBalanceUnlimitedToggle = (extensionId) => {
    setExtensionSettings(prev => {
      const newSettings = {
        ...prev,
        [extensionId]: {
          ...prev[extensionId],
          isExtensionBalanceUnlimited: !prev[extensionId]?.isExtensionBalanceUnlimited
        }
      };
      
      // Check if any extension is not unlimited
      const hasNonUnlimited = Object.values(newSettings).some(
        setting => !setting.isExtensionBalanceUnlimited
      );
      
      // Update master unlimited balance based on extension settings
      if (hasNonUnlimited && formData.isBalanceUnlimited) {
        setFormData(prev => ({
          ...prev,
          isBalanceUnlimited: false
        }));
      }
      
      return newSettings;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare assigned extensions with their settings
      const assignedExtensionsWithSettings = formData.assignedExtensions.map(extId => ({
        extensionId: extId,
        linkManager: extensionSettings[extId]?.linkManager || false,
        extensionBalance: extensionSettings[extId]?.isExtensionBalanceUnlimited ? 0 : (extensionSettings[extId]?.extensionBalance || 0),
        isExtensionBalanceUnlimited: extensionSettings[extId]?.isExtensionBalanceUnlimited || false
      }));

      // Prepare partner data for API
      const partnerData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        company: formData.company,
        partnerType: formData.partnerType,
        role: formData.role,
        planLevel: parseInt(formData.planLevel) || 0,
        balance: formData.isBalanceUnlimited ? 0 : (parseInt(formData.balance) || 0),
        isBalanceUnlimited: formData.isBalanceUnlimited,
        assignedExtensions: assignedExtensionsWithSettings
      };

      console.log('Sending partner data to API:', partnerData);

      const response = await partnersAPI.create(partnerData);
      console.log('API response:', response);
      
      if (response.success && response.defaultPassword) {
        toast.success('Partner added successfully!');
        alert(`Partner created successfully!\n\nDefault password: ${response.defaultPassword}\n\nPlease share this password with the partner. They can change it after logging in.`);
      } else {
        toast.success('Partner added successfully!');
      }
      
      navigate('/partners');
    } catch (error) {
      console.error('Failed to create partner:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      toast.error(error.message || 'Failed to add partner. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    navigate('/partners');
  };

  // Add loading state for the entire page
  if (loadingExtensions) {
    return (
      <div className="add-partner-container">
        <div className="page-header">
          <div className="page-title">
            <h2>Add a Partner</h2>
            <p>Please fill out the below form to add a partner under you</p>
          </div>
          <button className="close-btn" onClick={closeModal}>
            <FiX />
          </button>
        </div>
        <div className="add-partner-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading extensions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-partner-container">
      <div className="page-header">
        <div className="page-title">
          <h2>Add a Partner</h2>
          <p>Please fill out the below form to add a partner under you</p>
        </div>
        <button className="close-btn" onClick={closeModal}>
          <FiX />
        </button>
      </div>

      <div className="add-partner-content">
        <form onSubmit={handleSubmit}>
          <div className="addPartnerTopPart">
            <div className="partner_add_form">
              <div className="half">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="Enter"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Enter"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label>Partner's Email</label>
                <input
                  type="email"
                  placeholder="Enter"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label>Partner's Mobile No.</label>
                <input
                  type="tel"
                  placeholder="Enter"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label>Company</label>
                <input
                  type="text"
                  placeholder="Enter company name"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              <div className="half">
                <label>Partner Type</label>
                <select
                  name="partnerType"
                  value={formData.partnerType}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="ceo">CEO (Extension Creation)</option>
                  <option value="whitelabeler">Whitelabeler (Rebrand Access)</option>
                  <option value="reseller">Reseller (Sales & Distribution)</option>
                </select>
                <div className={`partnerTypeInfo ${formData.partnerType}`}>
                  {formData.partnerType === 'partner' && "Can create and sell extensions (includes whitelabeler and reseller abilities)"}
                  {formData.partnerType === 'whitelabeler' && "Can download and sell assigned whitelabeled extensions"}
                  {formData.partnerType === 'reseller' && "Can sell assigned extensions and manage EndUsers"}
                </div>
              </div>
              <div className="half">
                <label>Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="partner">Partner (Standard Access)</option>
                  <option value="admin">Admin (Enhanced Access)</option>
                  <option value="superadmin">Superadmin (Full Access)</option>
                </select>
                <div className={`roleInfo ${formData.role}`}>
                  {formData.role === 'partner' && "Standard partner access with basic permissions"}
                  {formData.role === 'admin' && "Admin access with enhanced permissions and user management"}
                  {formData.role === 'superadmin' && "Full system access with all permissions (use carefully)"}
                </div>
              </div>
              <div className="half">
                <label>Balance</label>
                <input
                  type="number"
                  name="balance"
                  value={formData.balance}
                  onChange={handleInputChange}
                  className="form-control"
                  min="0"
                  step="0.01"
                  disabled={formData.isBalanceUnlimited}
                />
                <div className="balanceInfo">
                  <label>
                    <input
                      type="checkbox"
                      name="isBalanceUnlimited"
                      checked={formData.isBalanceUnlimited}
                      onChange={handleInputChange}
                    />
                    Unlimited Balance
                  </label>
                </div>
              </div>
            </div>

            <div className="placeExtDeatils">
              <div className="headerExtTop">
                <div className="name">
                  <h3>Assign to an Extension</h3>
                  <div className="tagSpan">
                    {formData.assignedExtensions.length}/{Array.isArray(extensions) ? extensions.length : 0}
                  </div>
                </div>
                <div className="action">
                  <button 
                    type="button" 
                    className="dropAssignBtn"
                    onClick={() => {
                      if (!Array.isArray(extensions) || extensions.length === 0) return;
                      const allIds = extensions.map(ext => ext._id || ext.id);
                      setFormData(prev => ({
                        ...prev,
                        assignedExtensions: prev.assignedExtensions.length === allIds.length ? [] : allIds
                      }));
                    }}
                  >
                    {Array.isArray(extensions) && formData.assignedExtensions.length === extensions.length ? 'Unassign All' : 'Assign All'}
                  </button>
                  <input type="search" placeholder="Search" />
                </div>
              </div>

              <ul className="extListForPartner">
                {Array.isArray(extensions) && extensions.length > 0 ? (
                  extensions.map(extension => {
                    const extensionId = extension._id || extension.id;
                    const isAssigned = formData.assignedExtensions.includes(extensionId);
                    const settings = extensionSettings[extensionId] || {};
                    
                    return (
                      <li key={extensionId} className={isAssigned ? 'selected' : ''}>
                        <div className="state">
                          <button
                            type="button"
                            className={`addIt ${isAssigned ? 'selected' : ''}`}
                            onClick={() => handleExtensionToggle(extensionId)}
                          >
                            <FiPlus />
                          </button>
                        </div>
                        <div className="extDetails">
                          <div className="extName">
                            {extension.name}
                            {extension.isWhitelabeled && <span className="whiteTag">White Labeled</span>}
                          </div>
                          <div className="extPlans">
                            <p>Extension Type</p>
                            <div className="plan">
                              <span className="planName">{extension.category || 'Extension'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="linkManager">
                          <div className="balanceHead">
                            <span>Link Manager</span>
                          </div>
                          <div className="unlimited">
                            <Toggle
                              checked={settings.linkManager || false}
                              onChange={() => handleLinkManagerToggle(extensionId)}
                              disabled={!isAssigned}
                              icons={false}
                              className="ios-toggle"
                            />
                          </div>
                        </div>
                        <div className="balance">
                          <div className="balanceHead">
                            <span>Assign Balance</span>
                            <button 
                              type="button"
                              className="edit" 
                              onClick={() => {
                                // Toggle edit mode for balance
                                const newBalance = prompt('Enter balance for this extension:', settings.extensionBalance || 0);
                                if (newBalance !== null) {
                                  handleExtensionBalanceChange(extensionId, newBalance);
                                }
                              }}
                              disabled={!isAssigned}
                            >
                              <FiEdit />
                            </button>
                          </div>
                          <div className="score">
                            <span>{settings.isExtensionBalanceUnlimited ? '∞' : (settings.extensionBalance || 0)}</span>
                          </div>
                          <div className="unlimited">
                            <div className="text">Unlimited</div>
                            <Toggle
                              checked={settings.isExtensionBalanceUnlimited || false}
                              onChange={() => handleExtensionBalanceUnlimitedToggle(extensionId)}
                              disabled={!isAssigned}
                              icons={false}
                              className="ios-toggle"
                            />
                          </div>
                          {isAssigned && formData.isBalanceUnlimited && (
                            <div className="balance-sync-indicator">
                              <span>✓</span>
                              <span>Synced with master</span>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="no-extensions">
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      {loadingExtensions ? 'Loading extensions...' : 'No extensions available'}
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className="btnHolder">
            <button 
              type="submit" 
              className="btn large_submit_btn btn-large"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPartner; 